import { useCallback, useEffect, useRef, useState } from "react";

import { fetchTests } from "../api/tests";
import type { Test, TestListItem } from "../api/schemas";
import { NewTestWizard } from "./NewTestWizard";
import { TestDetailView } from "./TestDetailView";
import { TestsList, type LoadState } from "./TestsList";

function sortByUpdatedAtDesc(list: TestListItem[]): TestListItem[] {
  return [...list].sort(
    (a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at),
  );
}

function mergeTestIntoList(list: TestListItem[], fresh: Test): TestListItem[] {
  const { runs, ...listItem } = fresh;
  const idx = list.findIndex((t) => t.id === fresh.id);
  if (idx < 0) return list;
  const next = list.slice();
  // The detail payload doesn't carry the list-only activity aggregates:
  // derive active_runs from its runs, keep ar_active until the next
  // list poll (AR sessions aren't in the Test payload).
  next[idx] = {
    ...listItem,
    active_runs: runs.filter(
      (r) => r.status === "pending" || r.status === "running",
    ).length,
    ar_active: next[idx].ar_active,
  };
  return sortByUpdatedAtDesc(next);
}

export function TestsWorkspace() {
  const [tests, setTests] = useState<TestListItem[]>([]);
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [activeTestId, setActiveTestId] = useState<number | null>(null);
  const [view, setView] = useState<"wizard" | "detail">("wizard");
  // Studies list is collapsible so the work area can dominate (chart-centric
  // dashboard). A persistent gutter button toggles it.
  const [listCollapsed, setListCollapsed] = useState(false);
  const seededRef = useRef(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const list = await fetchTests();
      const sorted = sortByUpdatedAtDesc(list);
      setTests(sorted);
      setLoadState({ kind: "ready" });
      if (!seededRef.current) {
        seededRef.current = true;
        if (sorted.length === 0) {
          setView("wizard");
        } else {
          setActiveTestId(sorted[0].id);
          setView("detail");
        }
      }
    } catch (e) {
      // A transient poll failure must not blank an already-rendered
      // list — only surface the error before first successful load.
      setLoadState((prev) =>
        prev.kind === "ready"
          ? prev
          : {
              kind: "error",
              message: e instanceof Error ? e.message : String(e),
            },
      );
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Activity poll: keeps the per-study "running / AR" badges live while
  // the workspace is open. 8 s — the list endpoint is a light aggregate
  // query; the detail view separately polls its own study at 2 s.
  useEffect(() => {
    const handle = window.setInterval(() => {
      void refresh();
    }, 8000);
    return () => window.clearInterval(handle);
  }, [refresh]);

  const handleTestCreated = useCallback(
    async (createdId: number): Promise<void> => {
      await refresh();
      setActiveTestId(createdId);
      setView("detail");
    },
    [refresh],
  );

  const handleTestUpdated = useCallback((fresh: Test): void => {
    setTests((prev) => mergeTestIntoList(prev, fresh));
  }, []);

  const handleDetailClose = useCallback((): void => {
    setActiveTestId(null);
    setView("wizard");
  }, []);

  // After a hard delete: refetch the list (the deleted Test drops out) and
  // return the detail pane to the neutral wizard view.
  const handleTestDeleted = useCallback(async (): Promise<void> => {
    await refresh();
    setActiveTestId(null);
    setView("wizard");
  }, [refresh]);

  const handleSelect = useCallback((id: number): void => {
    setActiveTestId(id);
    setView("detail");
  }, []);

  const handleNewTest = useCallback((): void => {
    setActiveTestId(null);
    setView("wizard");
  }, []);

  return (
    <div
      className="tests-workspace"
      data-list-collapsed={listCollapsed ? "true" : "false"}
    >
      <div className="tests-list-gutter">
        <button
          type="button"
          className="tests-list-toggle"
          onClick={() => setListCollapsed((v) => !v)}
          title={listCollapsed ? "Show studies" : "Hide studies"}
          aria-label={listCollapsed ? "Show studies" : "Hide studies"}
          data-testid="toggle-studies-list"
        >
          {listCollapsed ? "≡" : "◂"}
        </button>
      </div>
      <TestsList
        tests={tests}
        selectedId={activeTestId}
        loadState={loadState}
        onNewTest={handleNewTest}
        onSelect={handleSelect}
      />
      <div className="tests-workspace-detail">
        {view === "detail" && activeTestId !== null ? (
          <TestDetailView
            testId={activeTestId}
            onClose={handleDetailClose}
            onTestUpdated={handleTestUpdated}
            onDeleted={handleTestDeleted}
          />
        ) : (
          <NewTestWizard onCreated={handleTestCreated} />
        )}
      </div>
    </div>
  );
}
